import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal } from 'react-bootstrap';
import { Prod01 } from '../../constants/ActionType';
import { parseScan } from '../../utils/imei';
import { FOREIGN_DEVICE_MESSAGE, errorText, isUnknownDevice } from '../../utils/deviceAssign';
import RegisterDeviceForm from './RegisterDeviceForm';

const fullName = (p) => [p?.firstname, p?.lastname].filter(Boolean).join(' ');

/** Patient profile "Assign device": scan/type IMEI -> confirm, or Register & assign when the IMEI is unknown. */
const AssignDeviceDialog = ({ show, patient, onHide, onAssigned }) => {
    const [scan, setScan] = useState('');
    const [step, setStep] = useState('scan');
    const [device, setDevice] = useState(null);
    const [msg, setMsg] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (show) { setScan(''); setStep('scan'); setDevice(null); setMsg(null); } }, [show]);

    const lookup = async (e) => {
        if (e) e.preventDefault();
        if (busy || !patient?._id) return;
        setMsg(null);
        const parsed = parseScan(scan);
        if (!parsed.imei) { setMsg({ v: 'danger', t: parsed.error || 'Enter a valid 15-digit IMEI' }); return; }
        setScan(parsed.imei);
        setBusy(true);
        try {
            const { data } = await axios.get(`${Prod01}/inventory/devices/lookup`, { params: { code: parsed.imei, patientId: patient._id } });
            if (data.status === 'available') { setDevice(data.device); setStep('confirm'); }
            else if (data.status === 'assigned_to_this_patient') setMsg({ v: 'info', t: 'This device is already assigned to this patient.' });
            else if (data.status === 'assigned_to_other' || data.status === 'wrong_org') setMsg({ v: 'warning', t: FOREIGN_DEVICE_MESSAGE });
            else setMsg({ v: 'danger', t: `Device cannot be assigned (${data.status}).` });
        } catch (err) {
            if (isUnknownDevice(err)) setStep('register');
            else setMsg({ v: err?.response?.status === 403 ? 'warning' : 'danger', t: errorText(err) });
        }
        setBusy(false);
    };

    const confirm = async () => {
        setBusy(true);
        try {
            await axios.post(`${Prod01}/inventory/assign`, { deviceId: device._id, patientId: patient._id });
            setBusy(false);
            onAssigned(`Device …${String(device.imei || '').slice(-5)} assigned to ${fullName(patient)}.`);
        } catch (err) {
            setBusy(false);
            setMsg({ v: err?.response?.status === 409 || err?.response?.status === 403 ? 'warning' : 'danger', t: errorText(err) });
        }
    };

    return (
        <Modal show={show} onHide={() => !busy && onHide()} centered>
            <Modal.Header closeButton><Modal.Title style={{ fontSize: 18 }}>Assign device — {fullName(patient)}</Modal.Title></Modal.Header>
            <Modal.Body data-testid="assign-device-dialog">
                {step === 'scan' ? (
                    <form onSubmit={lookup}>
                        <label htmlFor="ad-imei" className="mb-0">Scan or type the 15-digit IMEI</label>
                        <input id="ad-imei" className="form-control form-control-sm" inputMode="numeric" autoComplete="off" autoFocus
                            value={scan} disabled={busy} onChange={(e) => setScan(e.target.value.replace(/[\r\n\t]/g, ''))} />
                    </form>
                ) : null}
                {step === 'confirm' && device ? (
                    <p className="mb-0">Assign <strong>{device.deviceType || 'device'}</strong> · IMEI (last 5) <strong>{String(device.imei || '').slice(-5)}</strong> to <strong>{fullName(patient)}</strong>?</p>
                ) : null}
                {step === 'register' ? (
                    <RegisterDeviceForm imei={scan} patient={patient} onCancel={() => setStep('scan')} onDone={onAssigned} />
                ) : null}
                {msg ? <div className={`alert alert-${msg.v} py-1 mt-2 mb-0`} role="status" style={{ fontSize: 13 }}>{msg.t}</div> : null}
            </Modal.Body>
            {step !== 'register' ? (
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={step === 'confirm' ? () => setStep('scan') : onHide}>{step === 'confirm' ? 'Back' : 'Cancel'}</button>
                    {step === 'scan'
                        ? <button type="button" className="btn btn-primary btn-sm" disabled={busy || !scan.trim()} onClick={lookup}>Look up</button>
                        : <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={confirm}>Confirm assign</button>}
                </Modal.Footer>
            ) : null}
        </Modal>
    );
};

export default AssignDeviceDialog;
